/**
 * StatTile — canonical sysadmin stat strip primitive.
 *
 * Per SYSADMIN-PLATFORM-SPEC §11.3:
 *   - 4-up grid uses `grid-cols-2 md:grid-cols-4 gap-3`
 *   - `text-xs text-muted-foreground` label
 *   - `text-2xl font-semibold tabular-nums` value
 *   - Optional hint, optional tone (success/warning/destructive/muted)
 *   - NEVER wrapped in <Card> per tile — flat panel with 1px border + rounded-md
 *
 * Used across every sysadmin rail's overview/landing page (Phase 5
 * PR-A..F). Extracted from the inline SummaryTile pattern that grew
 * up inside `routes/sysadmin/runtime.health.tsx`.
 *
 * @example
 * <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
 *   <StatTile label="Healthy" value={11} suffix={`/ ${total}`} tone="success" />
 *   <StatTile label="Degraded" value={2} tone="warning" />
 *   <StatTile label="Down" value={0} tone="destructive" />
 *   <StatTile label="Unknown" value={0} tone="muted" />
 * </div>
 */

import type { ComponentType, ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

export type StatTileTone =
  | 'default'
  | 'success'
  | 'warning'
  | 'destructive'
  | 'muted';

export interface StatTileProps {
  /** Small uppercase-ish caption above the value */
  label: string;
  /** The headline value — string or number, rendered with tabular-nums */
  value: ReactNode;
  /** Optional suffix to follow the value (e.g. "/ 13") */
  suffix?: ReactNode;
  /** Optional secondary line (e.g. "3 down, 2 degraded") */
  hint?: ReactNode;
  /** Optional leading icon — appears next to the label */
  icon?: LucideIcon | ComponentType<{ className?: string }>;
  /** Tone drives the border/bg accent (per §11.12 single semantic palette) */
  tone?: StatTileTone;
  /** Optional click handler — when present the tile becomes a button (a11y) */
  onClick?: () => void;
  className?: string;
}

// Single semantic palette — must match `lib/status-tone` and stay in
// sync across every sysadmin rail per §11.12.
const TONE_STYLES: Record<StatTileTone, { border: string; iconText: string; valueText: string }> = {
  default: {
    border: 'border-border bg-background',
    iconText: 'text-muted-foreground',
    valueText: 'text-foreground',
  },
  success: {
    border: 'border-emerald-500/30 bg-emerald-500/5',
    iconText: 'text-emerald-600 dark:text-emerald-400',
    valueText: 'text-foreground',
  },
  warning: {
    border: 'border-amber-500/30 bg-amber-500/5',
    iconText: 'text-amber-600 dark:text-amber-400',
    valueText: 'text-foreground',
  },
  destructive: {
    border: 'border-destructive/30 bg-destructive/5',
    iconText: 'text-destructive',
    valueText: 'text-foreground',
  },
  muted: {
    border: 'border-muted bg-muted/20',
    iconText: 'text-muted-foreground',
    valueText: 'text-foreground',
  },
};

export function StatTile({
  label,
  value,
  suffix,
  hint,
  icon: Icon,
  tone = 'default',
  onClick,
  className,
}: StatTileProps) {
  const t = TONE_STYLES[tone];

  const body = (
    <>
      <div className="flex items-center gap-2">
        {Icon ? <Icon className={cn('h-4 w-4', t.iconText)} /> : null}
        <span className="text-xs text-muted-foreground">{label}</span>
      </div>
      <div className={cn('mt-1 text-2xl font-semibold tabular-nums leading-tight', t.valueText)}>
        {value}
        {suffix ? <span className="text-base text-muted-foreground"> {suffix}</span> : null}
      </div>
      {hint ? <div className="mt-1 text-[11px] text-muted-foreground">{hint}</div> : null}
    </>
  );

  // Per §11.3 — flat panel with 1px border, no Card wrap. Tiles are
  // visually distinct via tone-driven border/bg only.
  const baseClass = cn(
    'rounded-md border p-4',
    t.border,
    onClick && 'cursor-pointer hover:bg-accent/40 transition-colors text-left',
    className,
  );

  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={baseClass}>
        {body}
      </button>
    );
  }

  return <div className={baseClass}>{body}</div>;
}
